/**
 * Shared Research types.
 *
 * Single source of truth for the TYPED research outputs returned by the
 * research engine - both over the `research.completed` WebPubSub event and
 * in the persisted blob result loaded on history.
 *
 * The engine returns:
 *   { intent, payload_schema, payload, synthesis_markdown, citations[], ... }
 * with `final_response` / `text` (markdown) kept for backward compatibility.
 *
 * Everything here is intentionally tolerant: payload fields are optional so a
 * partially-populated or older payload still type-checks, and the adapter
 * (`./adapter`) is responsible for validating/normalising before the typed
 * leaf renderers consume it.
 */

/* ------------------------------------------------------------------ */
/*  Intent                                                             */
/* ------------------------------------------------------------------ */

export type ResearchIntent =
  | 'find_document'
  | 'overview'
  | 'deep_research'
  | 'chronology'
  | 'bullets'
  | 'memo'
  | 'draft'
  | 'qa';

export const RESEARCH_INTENTS: ResearchIntent[] = [
  'find_document',
  'overview',
  'deep_research',
  'chronology',
  'bullets',
  'memo',
  'draft',
  'qa',
];

export function isResearchIntent(value: unknown): value is ResearchIntent {
  return typeof value === 'string' && (RESEARCH_INTENTS as string[]).includes(value);
}

/* ------------------------------------------------------------------ */
/*  Citations                                                          */
/* ------------------------------------------------------------------ */

/** Where the character offsets on a citation came from. */
export type CitationOffsetSource = '' | 'chunker' | 'snippet_match' | 'unavailable';

/**
 * A single citation as returned by the engine.
 *
 * Structurally a superset of the legacy `Citation` interface in
 * `store/actions/ChatAction` (id/law/paragraph/section/snippet/title/url/number),
 * so a `ResearchCitation` is assignable anywhere a `Citation` is expected —
 * while carrying the new provenance fields that used to be dropped by the
 * ad-hoc re-mapping in `ChatInterface`.
 */
export interface ResearchCitation {
  /* ── legacy / display fields ───────────────────────────────── */
  id: string;
  number: number;
  title: string;
  snippet: string;
  section: string;
  law: string;
  paragraph: string;
  url: string;
  favicon?: string;

  /* ── new provenance fields ─────────────────────────────────── */
  provider: string;
  source_type: string;
  relevance_tier: string;
  score: number | null;
  start_char: number | null;
  end_char: number | null;
  offset_source: CitationOffsetSource;
  file_name: string;
  document_uuid: string;
  /**
   * Storage path of the cited document, when the engine supplies one.
   *
   * This is what `POST /matters/stream-source` needs to fetch and decrypt the
   * source for inline display. The engine does NOT currently put it on a
   * citation (see `research.py`'s citation construction), so it is usually
   * empty - the panel falls back to the snippet until it arrives.
   */
  blob_path: string;
  /** The index the hit came from - provenance, and a blob-path fallback key. */
  index_name: string;

  /* ── finalized citation contract ───────────────────────────── */
  /** The marker as it appears in the text, e.g. `[3]`. */
  marker: string;
  /** e.g. "Case law", "Legislation" - the engine's own wording. */
  document_type: string;
  /** Court or publisher. */
  authority: string;
  jurisdiction: string;
  date: string;
  document_number: string;
  /** Where in the document - "Kap. 4 § 12", "para. 17". */
  pinpoint: string;

  /* ── provenance: what the DOCUMENT says vs what the ANSWER claims ── */
  /**
   * THE VERBATIM PASSAGE - a span of the retrieved chunk and nothing else.
   *
   * This is the only text on a citation that the document actually contains,
   * and it is what may be rendered as a quotation. "" means the engine found no
   * locatable passage; render the source without a quote rather than falling
   * back to `asserts`, which is how BUG-01 happened.
   */
  quote: string;
  /** Offsets of `quote` inside the retrieved chunk. Null when unprovable. */
  quote_start: number | null;
  quote_end: number | null;
  /** True only when `chunk[quote_start:quote_end] === quote`, byte for byte. */
  quote_exact: boolean;
  /**
   * The sentence in the ANSWER that this marker sat in - what the source is
   * cited FOR. Model-generated: label it, never quote it.
   */
  asserts: string;
  /** Always `'model_generated'`. Provenance, stated so it cannot be mistaken. */
  asserts_kind: string;
  /**
   * @deprecated `asserts` under its old name. The engine emits both for one
   * release so stored blobs keep rendering; read `asserts`.
   */
  supports: string;
  /** The model's rationale for the citation. Generated. May be "". */
  justification: string;
  /** Storage container for `blob_path`; "" means fall back to `url`. */
  container: string;
  /** How to get to the cited passage inside the document. */
  locator: CitationLocator | null;
}

/**
 * How to find the cited passage in the rendered document.
 *
 * `strategy` names the STRONGEST form the engine could produce; the renderer
 * walks down from there, because what works depends on the browser and on
 * what the converted document actually contains.
 */
/**
 * One matching passage inside the full document.
 *
 * The engine orders these primary-first and excludes boilerplate, so the list
 * is rendered as received - `sections[0]` is the same passage the top-level
 * locator points at.
 */
export interface CitationSection {
  heading: string;
  start_char: number | null;
  end_char: number | null;
  precision: string;
  chunk_index: number | null;
  excerpt: string;
}

export interface CitationLocator {
  strategy: LocatorStrategy;
  /** A URL text fragment (`#:~:text=…`) - native scroll and highlight. */
  text_fragment: string;
  /** The literal passage, searched for in the rendered document. */
  quote: string;
  start_char: number | null;
  end_char: number | null;
  /** Offsets are only usable when this is not `unavailable`. */
  precision: string;
  /** A heading to scroll to. */
  heading: string;
  /** Nth chunk of the document. */
  chunk: number | null;
  /** Every matching passage, primary first. Often just the one. */
  sections: CitationSection[];
}

export type LocatorStrategy =
  | 'text_fragment'
  | 'quote'
  | 'char_range'
  | 'heading'
  | 'chunk'
  | 'none';

/** A character range inside the cited document that may be highlighted. */
export interface CitationHighlight {
  start: number;
  end: number;
}

/**
 * A request to open a citation in the existing document viewer / citation
 * panel. `highlight` is only set when the engine gave us chunker-grade
 * offsets - anything else links without a false-precision highlight.
 */
export interface CitationOpenRequest {
  citation: ResearchCitation;
  /** URL when the citation has one, else the citation label for the panel. */
  target: string;
  highlight: CitationHighlight | null;
}

/**
 * Offsets are only trustworthy when the engine located them with the
 * chunker. `snippet_match` / `unavailable` / '' must NOT highlight.
 */
export function citationHighlight(citation: ResearchCitation): CitationHighlight | null {
  if (citation.offset_source !== 'chunker') return null;
  const { start_char: start, end_char: end } = citation;
  if (typeof start !== 'number' || typeof end !== 'number') return null;
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  if (start < 0 || end <= start) return null;
  return { start, end };
}

/** Citations that point at a file the user uploaded themselves. */
export function isUploadedDocument(citation: ResearchCitation): boolean {
  return citation.provider === 'uploaded_document';
}

/* ------------------------------------------------------------------ */
/*  Payloads - mirror the engine payload schemas                       */
/* ------------------------------------------------------------------ */

/**
 * Citation references carried inside a payload node.
 *
 * The engine emits plain citation numbers; both the canonical `citations`
 * key and the longer `citation_numbers` spelling are accepted so a payload
 * from either revision renders.
 */
export interface CitationRefs {
  citations?: number[];
  citation_numbers?: number[];
}

/** `prose` schema - used by `overview` and `qa`. */
export interface ProsePayload extends CitationRefs {
  title?: string;
  /** The lead answer paragraph(s). `prose` is the older spelling. */
  answer?: string;
  prose?: string;
  key_points?: Array<string | ({ text?: string; point?: string } & CitationRefs)>;
  sections?: Array<{
    heading?: string;
    body?: string;
  } & CitationRefs>;
  caveats?: string[];
}

/* --- memo (also used for deep_research) --- */

export interface MemoKeyAuthority {
  citation: string;
  principle: string;
  weight: string;
  application_notes?: string;
}

export interface MemoAnalysisSection {
  topic: string;
  content: string;
  key_authorities?: MemoKeyAuthority[];
}

export interface MemoAuthorityGroup {
  summary: string;
  citations: string[];
}

export interface MemoUncertaintyArea {
  area: string;
  current_status: string;
  relevant_authorities?: string[];
  implications: string;
}

/** `memo` schema - the long-form legal memo (also produced for `deep_research`). */
export interface MemoPayload {
  original_question?: string;
  executive_summary?: {
    direct_answer: string;
    key_findings: string[];
    confidence_level: string;
    areas_of_uncertainty: string;
  };
  detailed_analysis?: Record<string, MemoAnalysisSection>;
  authority_integration?: {
    binding_authorities?: MemoAuthorityGroup;
    persuasive_authorities?: MemoAuthorityGroup;
  };
  jurisdictional_analysis?: {
    primary_jurisdiction: string;
    applicable_frameworks: string;
    variations_by_jurisdiction?: string;
    federal_vs_state?: string;
  };
  conflicting_authorities?: Array<{ description?: string } | string>;
  areas_of_uncertainty_and_development?: MemoUncertaintyArea[];
  gaps_and_limitations?: {
    information_gaps: string;
    limitation_summary: string;
    recommendations_for_deeper_research: string;
  };
  practical_implications?: Record<string, string>;
  /** Present on some blobs - the markdown rendering of the memo. */
  content?: string;
}

/**
 * `chronology` schema.
 *
 * Event order is fixed by the engine - renderers MUST preserve payload order
 * and never re-sort by date.
 */
export interface ChronologyEvent extends CitationRefs {
  date?: string;
  /** e.g. `day` | `month` | `year` | `unknown` */
  date_precision?: string;
  /** What happened. `title`/`description` are the older spellings. */
  event?: string;
  title?: string;
  description?: string;
  /** Why it matters. */
  significance?: string;
  actors?: string[];
}

export interface ChronologyPayload {
  title?: string;
  summary?: string;
  events?: ChronologyEvent[];
  gaps?: string[];
}

/** `bullets` schema. */
export interface BulletItem extends CitationRefs {
  /** Optional group label rendered above the point. */
  heading?: string;
  /** The bullet itself. `text` is the older spelling. */
  point?: string;
  text?: string;
  /** Optional supporting line under the point. */
  detail?: string;
  sub_bullets?: string[];
}

export interface BulletsPayload {
  title?: string;
  summary?: string;
  bullets?: BulletItem[];
  groups?: Array<{
    heading?: string;
    bullets?: BulletItem[];
  }>;
}

/**
 * `draft` schema - mirrors `SCHEMA_DRAFT` in the engine's
 * `app/services/research_output.py` field for field.
 *
 * `body_markdown` IS the deliverable: the instrument itself, in markdown, with
 * numbered clauses, headings and a signature block. Everything else annotates
 * it - the server renderer (`render_draft`) emits the body verbatim and first,
 * then the annotations below a rule, and this renderer keeps that hierarchy.
 */
export interface DraftParty {
  /** e.g. "Disclosing Party" */
  role?: string;
  name?: string;
}

export interface DraftPayload {
  /** What was produced, e.g. "Non-Disclosure Agreement". */
  document_type?: string;
  /** The document's own title line. */
  title?: string;
  /** ISO code or language name the document is written in. */
  language?: string;
  /** THE DOCUMENT ITSELF - markdown. */
  body_markdown?: string;
  parties?: DraftParty[];
  /** Anything inferred because it was not supplied. */
  assumptions?: string[];
  /** Every [BRACKETED] field the user must complete. */
  placeholders?: string[];
  /** Jurisdiction the draft is written for. */
  governing_law?: string;
  /** `[N]` markers for statutory/precedent grounding. */
  sources_relied_on?: string[];
  follow_up?: string[];
}

/**
 * R6 draft self-review (`research_verifier.review_draft`).
 * Annotation only - the engine never rewrites the draft on the back of it.
 */
export interface DraftReview {
  issues?: string[];
  /** [BRACKETED] fields found in the body but missing from `placeholders`. */
  placeholders_undeclared?: string[];
  score?: number | null;
  /** Comma-separated evaluator dimensions, when the shared evaluator ran. */
  dimensions?: string;
}

/**
 * R5 groundedness verification (`research_verifier.verify_answer`).
 * Also annotation only - `mode` is always `annotate`.
 */
export interface VerificationReport {
  confidence?: string;
  claims_checked?: number;
  unsupported?: Array<{ claim?: string; why?: string }>;
  dangling_citations?: number[];
  uncited_available?: number[];
  mode?: string;
}

/** `source_list` schema - used by `find_document`. */
export interface SourceListItem {
  number?: number;
  title?: string;
  /** Human-readable pointer into the document (§, article, page, clause). */
  locator?: string;
  file_name?: string;
  document_uuid?: string;
  url?: string;
  snippet?: string;
  why_relevant?: string;
  provider?: string;
  source_type?: string;
  relevance_tier?: string;
  score?: number | null;
}

export interface SourceListPayload {
  title?: string;
  summary?: string;
  documents?: SourceListItem[];
  total_found?: number;
}

/* ------------------------------------------------------------------ */
/*  Envelope                                                           */
/* ------------------------------------------------------------------ */

/** How the intent on an envelope was determined. */
export type IntentSource = 'explicit' | 'server' | 'inferred' | 'fallback';

export interface ResearchMeta {
  /**
   * `explicit` - the user pinned it on the composer; `server` - the engine
   * classified it; `inferred` - derived from the payload shape;
   * `fallback` - defaulted. Anything but `explicit` is worth showing back to
   * the user as "detected", so they can re-run as another type.
   */
  intentSource: IntentSource;
  messageUuid?: string;
  /** `payload_schema` as sent by the engine, when present. */
  payloadSchema?: string;
  /** False when the typed payload was missing/malformed - renderers must use `markdown`. */
  payloadValid: boolean;
  correlationId?: string;
  chatName?: string;
  processingTimeMs?: number;
  modelUsed?: string;
  /** Retrieval block from the blob, used by the citation side panel. */
  retrieval?: Record<string, any>;
  /**
   * R5/R6 annotations. These arrive as SIBLINGS of `payload` on the event and
   * the blob, not inside it, so the adapter lifts them here.
   */
  verification?: VerificationReport;
  draftReview?: DraftReview;
  /**
   * The engine's own verdict on whether this turn is a document (card + pane)
   * or a conversational reply. Authoritative when present - it comes from the
   * same classification that chose the schema, so the UI never second-guesses
   * it with an intent list of its own.
   */
  isArtifact?: boolean;
  /** The untouched source object the envelope was built from. */
  raw?: unknown;
}

export interface Envelope<K extends ResearchIntent, P> {
  intent: K;
  payload: P;
  /** `synthesis_markdown` (server-rendered) with `final_response`/`text` fallback. Always a string. */
  markdown: string;
  citations: ResearchCitation[];
  /**
   * The conversational lines around a document, written by the model in the
   * session's output language.
   *
   * All three are engine-generated and empty when the model declined. The UI
   * renders each only when non-empty and NEVER substitutes copy of its own —
   * a static English sentence would be wrong in a Norwegian session, and
   * canned text reads as canned.
   */
  leadIn: string;
  closing: string;
  /**
   * Short conversational lines the engine emitted DURING the run, in order.
   * Persisted on the blob, so a reloaded turn reads as it did live.
   */
  interim: string[];
  /**
   * The engine's tailored progress phrases for this question.
   *
   * Not part of the answer - the working indicator's vocabulary while the
   * run is in flight. Persisted so a reload of a turn that is STILL running
   * gets them back; on a finished turn nothing reads them.
   */
  cues: string[];
  /**
   * The document's own title, written by the model.
   *
   * Empty when the engine has none - it is deliberately NOT backfilled from
   * `chat_title` server-side, so an empty value means "no title", not "look
   * it up somewhere else". The UI falls back on its own terms.
   */
  documentTitle: string;
  followUp: string[];
  meta: ResearchMeta;
}

/** Discriminated union of every typed research response. */
export type ResearchResponse =
  | Envelope<'memo', MemoPayload>
  | Envelope<'deep_research', MemoPayload>
  | Envelope<'overview', ProsePayload>
  | Envelope<'qa', ProsePayload>
  | Envelope<'chronology', ChronologyPayload>
  | Envelope<'bullets', BulletsPayload>
  | Envelope<'find_document', SourceListPayload>
  | Envelope<'draft', DraftPayload>;

/** Intents that render with the memo visual. */
export function isMemoResponse(
  response: ResearchResponse
): response is Envelope<'memo', MemoPayload> | Envelope<'deep_research', MemoPayload> {
  return response.intent === 'memo' || response.intent === 'deep_research';
}
